// Geração de PDFs em "papel timbrado" (pedido de exame, plano alimentar, receitas),
// no padrão dos documentos que a Bárbara já emite fora do app: logo + dados da
// nutricionista no topo, título + nome da aluna, corpo do documento, rodapé com
// nome/CRN da nutricionista em toda página.
import jsPDF from 'jspdf';
import { formatQuantidade } from './dietaUtils';
import { QUESTIONARIOS, interpretacaoRastreamento } from './questionarios';

const NUTRICIONISTA = {
  nome: 'Barbara Macedo Bitencourt',
  especialidade: 'Nutrição Funcional e Comportamental',
  telefone: '11 95884 5786',
  email: 'Babimacedob@gmail.com',
  crn: 'CRN: 79958',
};

const MARGIN = 48;
const COR_TEXTO = [37, 28, 25];
const COR_TEXTO_2 = [110, 98, 90];
const COR_ACCENT = [137, 91, 64];

async function carregarLogoBase64() {
  try {
    const res = await fetch('/logo.jpg');
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function criarWriter(doc, logoBase64) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const w = {
    doc, pageWidth, pageHeight,
    y: MARGIN,
    desenharCabecalho() {
      if (logoBase64) {
        try { doc.addImage(logoBase64, 'JPEG', MARGIN, MARGIN, 36, 36); } catch { /* ignora logo inválida */ }
      }
      const xInfo = pageWidth - MARGIN;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...COR_TEXTO);
      doc.text(NUTRICIONISTA.nome, xInfo, MARGIN + 8, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...COR_TEXTO_2);
      doc.text(NUTRICIONISTA.especialidade, xInfo, MARGIN + 20, { align: 'right' });
      doc.text(NUTRICIONISTA.telefone, xInfo, MARGIN + 31, { align: 'right' });
      doc.text(NUTRICIONISTA.email, xInfo, MARGIN + 42, { align: 'right' });
      w.y = MARGIN + 62;
      doc.setDrawColor(210, 198, 182);
      doc.line(MARGIN, w.y, pageWidth - MARGIN, w.y);
      w.y += 28;
    },
    desenharRodape() {
      const totalPaginas = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPaginas; p++) {
        doc.setPage(p);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...COR_TEXTO_2);
        doc.text(`Nutricionista ${NUTRICIONISTA.nome} · ${NUTRICIONISTA.crn}`, pageWidth / 2, pageHeight - 24, { align: 'center' });
        doc.text(`${p}/${totalPaginas}`, pageWidth - MARGIN, pageHeight - 24, { align: 'right' });
      }
    },
    novaPagina() {
      doc.addPage();
      w.desenharCabecalho();
    },
    espaco(altura) {
      if (w.y + altura > w.pageHeight - MARGIN - 30) w.novaPagina();
    },
    titulo(texto) {
      w.espaco(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...COR_TEXTO);
      doc.text(texto, pageWidth / 2, w.y, { align: 'center' });
      w.y += 22;
    },
    subtitulo(texto) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COR_TEXTO_2);
      doc.text(texto, pageWidth / 2, w.y, { align: 'center' });
      w.y += 14;
      doc.setDrawColor(225, 216, 204);
      doc.line(MARGIN, w.y, pageWidth - MARGIN, w.y);
      w.y += 24;
    },
    secao(texto) {
      w.espaco(28);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...COR_ACCENT);
      doc.text(texto, MARGIN, w.y);
      w.y += 16;
    },
    paragrafo(texto, opts = {}) {
      if (!texto) return;
      doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
      doc.setFontSize(opts.size || 10);
      doc.setTextColor(...(opts.cor || COR_TEXTO));
      const linhas = doc.splitTextToSize(texto, pageWidth - MARGIN * 2 - (opts.indent || 0));
      linhas.forEach(linha => {
        w.espaco(14);
        doc.text(linha, MARGIN + (opts.indent || 0), w.y);
        w.y += 14;
      });
      w.y += opts.gapDepois ?? 8;
    },
    bullet(texto) {
      w.espaco(14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COR_TEXTO);
      const linhas = doc.splitTextToSize(texto, pageWidth - MARGIN * 2 - 14);
      doc.setTextColor(...COR_ACCENT);
      doc.text('•', MARGIN, w.y);
      doc.setTextColor(...COR_TEXTO);
      linhas.forEach((linha, i) => {
        if (i > 0) w.espaco(14);
        doc.text(linha, MARGIN + 14, w.y);
        w.y += 14;
      });
      w.y += 2;
    },
  };
  w.desenharCabecalho();
  return w;
}

async function salvar(construir, nomeArquivo) {
  const logoBase64 = await carregarLogoBase64();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const w = criarWriter(doc, logoBase64);
  construir(w);
  w.desenharRodape();
  doc.save(nomeArquivo);
}

export async function gerarPdfExame(exame, alunaNomeBruto) {
  const alunaNome = alunaNomeBruto || 'Aluna';
  await salvar((w) => {
    w.titulo(exame.titulo || 'Pedido de exame');
    w.subtitulo(`Nome do paciente: ${alunaNome}`);
    (exame.categorias || []).forEach(cat => {
      if (cat.titulo) w.secao(cat.titulo);
      (cat.exames || []).forEach(nome => w.paragrafo(nome, { gapDepois: 4 }));
      w.y += 8;
    });
    if (exame.orientacoesColeta) {
      w.secao('Orientações para coleta');
      w.paragrafo(exame.orientacoesColeta);
    }
    if (exame.obs) {
      w.secao('Observações');
      w.paragrafo(exame.obs);
    }
  }, `pedido-de-exame-${alunaNome.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}

export async function gerarPdfPlanoAlimentar(dieta, refeicoes, alunaNomeBruto) {
  const alunaNome = alunaNomeBruto || 'Aluna';
  await salvar((w) => {
    w.titulo(dieta.titulo || 'Plano alimentar');
    w.subtitulo(`Nome do paciente: ${alunaNome}`);

    if (dieta.orientacoes?.length > 0) {
      w.secao('Orientações');
      dieta.orientacoes.forEach(o => w.bullet(o));
      w.y += 8;
    }
    if (dieta.descricao) w.paragrafo(dieta.descricao, { gapDepois: 10 });
    const macros = [dieta.calorias && `Calorias: ${dieta.calorias}`, dieta.proteina && `Proteína: ${dieta.proteina}`].filter(Boolean).join('   ·   ');
    if (macros) w.paragrafo(macros, { bold: true, gapDepois: 10 });
    if (dieta.agua?.instrucao || dieta.agua?.meta) {
      w.secao('Água');
      if (dieta.agua.instrucao) w.paragrafo(dieta.agua.instrucao);
      if (dieta.agua.meta) w.paragrafo(`Meta diária: ${dieta.agua.meta}`, { bold: true });
      w.y += 4;
    }

    w.secao('Refeições');
    refeicoes.forEach(ref => {
      w.espaco(18);
      const titulo = ref.horario ? `${ref.nome} — ${ref.horario}` : ref.nome;
      w.paragrafo(titulo, { bold: true, size: 11, gapDepois: 2 });
      if (ref.observacao) w.paragrafo(ref.observacao, { size: 9, cor: COR_TEXTO_2, gapDepois: 4 });
      ref.opcoes.forEach(op => {
        if (op.titulo) w.paragrafo(op.titulo, { bold: true, size: 9, cor: COR_ACCENT, gapDepois: 2 });
        op.itens.forEach(item => {
          const qtd = formatQuantidade(item);
          w.paragrafo(`${item.nome}${qtd ? ` — ${qtd}` : ''}`, { indent: 10, gapDepois: 2 });
          if (item.observacao) w.paragrafo(item.observacao, { indent: 10, size: 9, cor: COR_TEXTO_2, gapDepois: 2 });
        });
        if (op.receita) w.paragrafo(`Receita: ${op.receita}`, { indent: 10, size: 9, cor: COR_TEXTO_2, gapDepois: 4 });
      });
      w.y += 6;
    });

    if (dieta.suplementacao?.length > 0) {
      w.secao('Suplementação');
      dieta.suplementacao.forEach(s => {
        const linha = [s.nome, s.dose, s.momento].filter(Boolean).join(' — ');
        w.paragrafo(linha, { gapDepois: 4 });
      });
    }
  }, `plano-alimentar-${alunaNome.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}

export async function gerarPdfReceitas(receitas, tituloDieta, alunaNomeBruto) {
  const alunaNome = alunaNomeBruto || 'Aluna';
  await salvar((w) => {
    w.titulo('Receitas');
    w.subtitulo(`Nome do paciente: ${alunaNome}${tituloDieta ? ` · ${tituloDieta}` : ''}`);
    receitas.forEach(r => {
      w.secao(r.opcao ? `${r.refeicao} · ${r.opcao}` : r.refeicao);
      w.paragrafo(r.receita, { gapDepois: 14 });
    });
  }, `receitas-${alunaNome.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}

export async function gerarPdfQuestionario(reg, alunaNomeBruto) {
  const q = QUESTIONARIOS[reg.tipo];
  if (!q) return;
  const alunaNome = alunaNomeBruto || 'Aluna';
  const dataResposta = reg.created_at
    ? new Date(reg.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  await salvar((w) => {
    w.titulo(q.titulo);
    w.subtitulo(`Nome do paciente: ${alunaNome}${dataResposta ? ` · Respondido em ${dataResposta}` : ''}`);

    if (q.secoes) {
      // escala (0-4) e frequência: respostas agrupadas por seção
      q.secoes.forEach(secao => {
        const valores = reg.respostas?.[secao.id] || [];
        const subtotal = q.modo === 'escala'
          ? valores.reduce((acc, v) => acc + (v || 0), 0)
          : null;
        w.secao(subtotal != null ? `${secao.titulo} — ${subtotal} pts` : secao.titulo);
        secao.itens.forEach((item, idx) => {
          const v = valores[idx];
          w.paragrafo(`${item}: ${v != null && v !== '' ? v : '—'}`, { gapDepois: 2 });
        });
        w.y += 6;
      });
      if (q.modo === 'escala' && reg.total != null) {
        const interp = interpretacaoRastreamento(reg.total);
        w.secao('Pontuação total');
        w.paragrafo(`${reg.total} pontos — ${interp.label}`, { bold: true });
      }
    } else {
      // anamnese (aberto) e disbiose (múltipla escolha): lista de perguntas
      const respostas = Array.isArray(reg.respostas) ? reg.respostas : [];
      q.perguntas.forEach((p, idx) => {
        w.paragrafo(`${idx + 1}) ${p.label}`, { bold: true, gapDepois: 2 });
        const v = respostas[idx];
        w.paragrafo(v != null && v !== '' ? String(v) : '— sem resposta —', { indent: 14, gapDepois: 10 });
      });
    }
  }, `questionario-${reg.tipo}-${alunaNome.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}
